import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useGameStartSequence - Hook for managing loading + countdown sequence
 */
const useGameStartSequence = ({
  assetsLoaded = false,
  countdownFrom = 3,
  countdownInterval = 1000,
  delayAfterCountdown = 200,
  onGameStart,
} = {}) => {
  // Start in countdown phase if assets already loaded, otherwise loading
  const [phase, setPhase] = useState(() => assetsLoaded ? 'countdown' : 'loading');
  const [countdown, setCountdown] = useState(countdownFrom);
  
  const timerRef = useRef(null);

  // Clear any existing timer
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Transition from loading to countdown when assets become ready
  useEffect(() => {
    if (assetsLoaded && phase === 'loading') {
      // Assets loaded — jump straight to countdown (no artificial delay)
      setPhase('countdown');
      setCountdown(countdownFrom);
    }
  }, [assetsLoaded, phase, countdownFrom]);

  // Handle countdown ticks
  useEffect(() => {
    if (phase !== 'countdown') return;

    if (countdown > 0) {
      timerRef.current = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, countdownInterval);
    } else {
      // Countdown finished, start game
      timerRef.current = setTimeout(() => {
        setPhase('playing');
        if (onGameStart) {
          onGameStart();
        }
      }, delayAfterCountdown);
    }

    return () => clearTimer();
  }, [countdown, phase, countdownInterval, delayAfterCountdown, onGameStart, clearTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  const isLoading = phase === 'loading';
  const gameStarted = phase === 'playing';
  const showOverlay = phase !== 'playing';

  return {
    isLoading,
    countdown,
    gameStarted,
    showOverlay,
  };
};

export default useGameStartSequence;
